import {kFakeFlashGrades, kFakeQuestions, kFakeSubthemesWithLevelAs} from "../../../services/Faker";
import {get_flash_grades} from "../../../api/grade";
import {isArrayNullUndefinedOrEmpty, isNullOrUndefined} from "../../../services/Validator";
import {get_subthemes_with_levelAs_from_grade} from "../../../api/subtheme";
import {get_questions_from_levelAsIds} from "../../../api/question";
import {useEffect, useState} from "react";
import {
    kSystemGradesError,
    kFlashQuestionsError, kFlashSubthemesError, kEmptyResponseError, kExerciserFetchError
} from "../../../constants/errors";
import SystemScreenLoader from "../../loaders/SystemScreenLoader";
import {sendErrorToSentry} from "../../../services/Sentry";
import {Environment} from "../../../enums/Environment";
import {isNumber} from "chart.js/helpers";
import {get_exerciser_problem} from "../../../api/levelA";
import CoursesCoreContent from "./CoursesCoreContent";
import SystemApiFullScreenError from "../../errors/SystemApiFullScreenError";

export default function CoursesContent({ selectedGrade,
                                         setFlashGrade,
                                         fetchQuestionsCallback,
                                         fetchExerciserProblemCallback }) {
    // Used in CoursesScreen to handle flash parameters selection
    // which is made of Grade and Subthemes selection

    // PROPERTIES
    // ---- Grades
    const [isGradesLoading, setIsGradesLoading] = useState(true)
    const [gradesError, setGradesError] = useState(null)
    const [grades, setGrades] = useState([])
    // ---- Subthemes
    const [isSubthemesLoading, setIsSubthemesLoading] = useState(false)
    const [subthemesError, setSubthemesError] = useState(null)
    const [subthemes, setSubthemes] = useState([])
    const [selectedSubtheme, setSelectedSubtheme] = useState(null)
    const [levelAs, setLevelAs] = useState([])
    const [selectedLevelAs, setSelectedLevelAs] = useState([])
    // ---- Questions
    const [isFlashLoading, setIsFlashLoading] = useState(false)
    const [flashError, setFlashError] = useState(null)
    // ---- Problems
    const [isExerciserLoading, setIsExerciserLoading] = useState(false)
    const [exerciserError, setExerciserError] = useState(null)

    // HOOKS
    // ---- Triggered at rendering : set Grades
    useEffect(() => {
        getFlashGrades()
    }, [])

    // BUILD
    if (!isNullOrUndefined(gradesError))
        return <SystemApiFullScreenError error={gradesError}
                                         isLoading={isGradesLoading}
                                         retryCallback={() => getFlashGrades()} />
    if (!isNullOrUndefined(flashError))
        return <SystemApiFullScreenError error={flashError}
                                         isLoading={isFlashLoading}
                                         retryCallback={() => fetchFlashQuestions()} />
    if (!isNullOrUndefined(exerciserError))
        return <SystemApiFullScreenError error={exerciserError}
                                         isLoading={isExerciserLoading}
                                         retryCallback={() => fetchExerciserProblem()} />
    if (isGradesLoading)
        return <SystemScreenLoader />
    return (
        <div className={"animate-appearSlow w-full grow flex flex-col justify-start items-center pb-[24px]"}>
            <CoursesCoreContent grades={grades}
                                  selectedGrade={selectedGrade}
                                  setFlashGrade={getSubthemesWithLevelAsFromGrade}
                                  isSubthemesLoading={isSubthemesLoading}
                                  subthemesError={subthemesError}
                                  subthemes={subthemes}
                                  selectSubtheme={selectSubtheme}
                                  selectedSubtheme={selectedSubtheme}
                                  levelAs={levelAs}
                                  selectedLevelAs={selectedLevelAs}
                                  pickLevelA={pickLevelA}
                                  clearLevelAs={clearLevelAs}
                                  // ---- Fetch flash questions
                                  isFlashLoading={isFlashLoading}
                                  fetchFlashQuestions={fetchFlashQuestions}
                                  // ---- Fetch exerciser questions
                                  isExerciserLoading={isExerciserLoading}
                                  fetchExerciserProblem={fetchExerciserProblem} />
        </div>
    );

    // METHODS
    // ---- API : Fetch flash levels from the api
    function getFlashGrades() {
        // <<<< For .env.faker ----
        if (process.env.REACT_APP_ENV === Environment.FAKER) {
            setGrades(kFakeFlashGrades)
            setIsGradesLoading(false)
            setGradesError(null)
            return
        }
        // ---- For .env.faker >>>>

        // Switch loader on and clear error
        setIsGradesLoading(true)
        setGradesError(null)
        // Perform
        get_flash_grades()
            .then((res) => {
                if (isNullOrUndefined(res) || isArrayNullUndefinedOrEmpty(res)) {
                    setGradesError(kEmptyResponseError)
                    sendErrorToSentry(kEmptyResponseError.sentry, "fetching Grades from CoursesContent")
                } else {
                    setGrades(res)
                }
            }).catch((err) => {
                console.error(err)
                setGradesError(kSystemGradesError)
                sendErrorToSentry(kSystemGradesError.sentry, "CoursesContent")
        }).finally(() => {
            setIsGradesLoading(false)
        })
    }

    // ---- API : Fetch Subthemes with LevelAs based on a specific Grade and Theme
    function getSubthemesWithLevelAsFromGrade(selectedGrade) {
        // <<<< For .env.faker ----
        if (process.env.REACT_APP_ENV === Environment.FAKER) {
            setSubthemes(kFakeSubthemesWithLevelAs)
            setFlashGrade(selectedGrade)
            setIsSubthemesLoading(false)
            setSubthemesError(null)
            return
        }
        // ---- For .env.faker >>>>

        // Check input parameters
        if (isNullOrUndefined(selectedGrade?.id))
            return;
        // Perform setFlashGrade from MetaCoursesScreen
        setFlashGrade(selectedGrade)
        // Reset screen state
        resetScreenState()
        // Switch loader on and clear error
        setIsSubthemesLoading(true)
        setSubthemesError(null)
        // Perform request
        get_subthemes_with_levelAs_from_grade(selectedGrade?.id)
            .then((res) => {
                if (isNullOrUndefined(res) || isArrayNullUndefinedOrEmpty(res)) {
                    setSubthemesError(kEmptyResponseError)
                    sendErrorToSentry(kEmptyResponseError.sentry, "fetching Subthemes from CoursesContent")
                } else {
                    setSubthemes(res)
                }
            }).catch((err) => {
            console.error(err)
            setSubthemesError(kFlashSubthemesError)
            sendErrorToSentry(kFlashSubthemesError.sentry)
        }).finally(() => {
            setIsSubthemesLoading(false)
        })
    }

    // ---- API : fetch questions
    function fetchFlashQuestions() {
        // <<<< For .env.faker ----
        if (process.env.REACT_APP_ENV === Environment.FAKER) {
            fetchQuestionsCallback(kFakeQuestions)
            setIsFlashLoading(false)
            setFlashError(null)
            return
        }
        // ---- For .env.faker >>>>
        // If no levelId or already loading, return
        if (isArrayNullUndefinedOrEmpty(selectedLevelAs) || isFlashLoading)
            return
        // Switch loader on and clear error
        setIsFlashLoading(true)
        setFlashError(null)
        // Prepare request body
        let data = {
            levelAsIds: selectedLevelAs.map(levelA => levelA.id)
        }
        // Perform
        get_questions_from_levelAsIds(data)
            .then((res) => {
                // Handle response
                if (isNullOrUndefined(res) || isArrayNullUndefinedOrEmpty(res)) {
                    setFlashError(kEmptyResponseError)
                    sendErrorToSentry(kEmptyResponseError.sentry, 'in fetching Questions from CoursesContent')
                } else {
                    fetchQuestionsCallback(res)
                }
            })
            .catch((err) => {
                console.log(err)
                setFlashError(kFlashQuestionsError)
                sendErrorToSentry(kFlashQuestionsError.sentry)
            })
            .finally(() => {
                setIsFlashLoading(false)
            })
    }

    // ---- API : Fetch exerciser problems
    function fetchExerciserProblem(levelAIdentifier) {
        // Defense
        // ---- Current screen URL identifier parameter
        if (isNullOrUndefined(levelAIdentifier) || !isNumber(levelAIdentifier) || levelAIdentifier < 0) {
            setIsExerciserLoading(false)
            setExerciserError("Malformed id parameter")
            return
        }

        // ---- Set loader on and clear potential error (retry)
        setIsExerciserLoading(true)
        setExerciserError(null)

        // Perform
        get_exerciser_problem(levelAIdentifier)
            .then((res) => {
                fetchExerciserProblemCallback(levelAIdentifier, res)
            }).catch((err) => {
            setExerciserError(kExerciserFetchError)
            console.error(err)
        }).finally(() => {
            setIsExerciserLoading(false)
        })
    }

    // ---- ---- ----
    // USER INTERACTION
    // ---- ---- ----
    // ---- LOGIC : Add picked levelA to selectedLevelAs (replace toggleLevelA)
    function pickLevelA(currentLevelA) {
        setSelectedLevelAs([currentLevelA]);
    }

    // ---- LOGIC : Clear selectedLevelAs (replace toggleLevelA)
    function clearLevelAs() {
        setSelectedLevelAs([])
    }

    // ---- Select a specific subtheme
    function selectSubtheme(subtheme) {
        // Check input parameter
        if (isNullOrUndefined(subtheme))
            return
        // Set current selected Subtheme
        setSelectedSubtheme(subtheme)
        // Set current displayed LevelAs
        setLevelAs(subtheme["subthemeLevelAs"])
    }

    // ---- ---- ----
    // OTHERS
    // ---- ---- ----
    // ---- Reset screen global state
    function resetScreenState() {
        setSubthemes([])
        setSelectedSubtheme(null)
        setLevelAs([])
        setSelectedLevelAs([])
    }
}